/**
 * Notification API Hooks
 *
 * TanStack Query hooks for fetching and mutating notifications.
 * Syncs server state with the Zustand notification store.
 */

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import {
  listNotifications,
  getNotificationUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "@/services/api/sdk.gen";
import { useNotificationStore } from "@/stores/useNotificationStore";
import type { AppNotification } from "@/stores/useNotificationStore";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";

// ============================================================
// API Response Types
// ============================================================

interface NotificationResponse {
  notificationId: string;
  typeKey: string;
  category: string;
  title: string;
  message: string;
  severity: string;
  actionUrl: string | null;
  actionLabel: string | null;
  metadata: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  projectId: string | null;
  workflowId: string | null;
  assetId: string | null;
}

interface NotificationsListData {
  data: NotificationResponse[];
  meta: {
    unreadCount: number;
    limit: number;
    offset: number;
  };
}

// ============================================================
// Helpers
// ============================================================

function transformNotification(n: NotificationResponse): AppNotification {
  return {
    id: n.notificationId,
    type: n.typeKey,
    title: n.title,
    message: n.message,
    severity: n.severity as AppNotification["severity"],
    actionUrl: n.actionUrl ?? undefined,
    actionLabel: n.actionLabel ?? undefined,
    metadata: n.metadata,
    isRead: n.isRead,
    createdAt: new Date(n.createdAt),
  };
}

// ============================================================
// Hooks
// ============================================================

interface FetchOptions {
  limit?: number;
  unreadOnly?: boolean;
  category?: string;
}

/**
 * Infinite-scroll notification list.
 * Syncs fetched data into the Zustand store.
 */
export function useNotificationList(options: FetchOptions = {}) {
  const { limit = 20, unreadOnly = false, category } = options;
  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);
  const setLoading = useNotificationStore((s) => s.setLoading);
  const setHasMore = useNotificationStore((s) => s.setHasMore);

  const query = useInfiniteQuery({
    queryKey: ["notifications", { unreadOnly, category }],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await listNotifications({
        throwOnError: true,
        query: {
          limit,
          offset: pageParam as number,
          ...(unreadOnly && { unreadOnly: "true" as const }),
          ...(category && {
            category: category as "execution" | "asset" | "team" | "system",
          }),
        },
      });

      return result.data as unknown as NotificationsListData;
    },
    getNextPageParam: (lastPage, allPages) => {
      // Safety check for undefined/empty responses
      if (!lastPage?.data) return undefined;
      const totalFetched = allPages.flatMap((p) => p.data ?? []).length;
      if (lastPage.data.length < limit) return undefined;
      return totalFetched;
    },
    initialPageParam: 0,
  });

  // Sync with Zustand store
  useEffect(() => {
    if (query.data) {
      const all = query.data.pages.flatMap((page) =>
        (page.data ?? []).map(transformNotification),
      );
      setNotifications(all);

      const lastPage = query.data.pages[query.data.pages.length - 1];
      setUnreadCount(lastPage?.meta?.unreadCount ?? 0);
      setHasMore(query.hasNextPage ?? false);
    }
    setLoading(query.isLoading);
  }, [
    query.data,
    query.isLoading,
    query.hasNextPage,
    setNotifications,
    setUnreadCount,
    setLoading,
    setHasMore,
  ]);

  return {
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}

/**
 * Lightweight unread count with polling fallback.
 * WebSocket events update the store in real time; this polls as a safety net.
 */
export function useUnreadCount() {
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);
  const storeCount = useNotificationStore((s) => s.unreadCount);

  const query = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const result = await getNotificationUnreadCount({ throwOnError: true });
      const responseData = result.data as unknown as {
        data: { count: number };
      };
      const count = responseData?.data?.count;
      if (count === undefined) {
        logger.warn("Unexpected unread count response", { data: result.data });
        return 0;
      }
      return count;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data !== undefined) {
      setUnreadCount(query.data);
    }
  }, [query.data, setUnreadCount]);

  return {
    unreadCount: query.data ?? storeCount,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/**
 * Notification mutations with optimistic updates.
 */
export function useNotificationMutations() {
  const queryClient = useQueryClient();
  const markAsReadStore = useNotificationStore((s) => s.markAsRead);
  const markAllAsReadStore = useNotificationStore((s) => s.markAllAsRead);
  const deleteNotificationStore = useNotificationStore(
    (s) => s.deleteNotification,
  );

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await markNotificationRead({
        throwOnError: true,
        path: { notificationId },
      });
      return notificationId;
    },
    onMutate: (notificationId) => {
      markAsReadStore(notificationId);
    },
    onError: (_error, _notificationId) => {
      logger.error("Failed to mark notification read", _error as Error, {
        correlationId: generateUUID(),
      });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await markAllNotificationsRead({ throwOnError: true });
    },
    onMutate: () => {
      markAllAsReadStore();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await deleteNotification({
        throwOnError: true,
        path: { notificationId },
      });
      return notificationId;
    },
    onMutate: (notificationId) => {
      deleteNotificationStore(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return {
    markAsRead: markReadMutation.mutate,
    markAllAsRead: markAllReadMutation.mutate,
    deleteNotification: deleteMutation.mutate,
    isMarkingRead: markReadMutation.isPending,
    isMarkingAllRead: markAllReadMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
